import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  LineChart, Line, AreaChart, Area, ResponsiveContainer,
  XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { api } from "@/lib/api";
import { fmtTRY, fmtMonth } from "@/lib/format";
import { ArrowDown, ArrowUp, Wallet, CalendarClock, Plus, ListChecks, BookOpen, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { TodoRow } from "@/pages/Todos";
import TurkeyMap from "@/components/TurkeyMap";
import ProfitTrendCard from "@/components/ProfitTrendCard";

const CHART_COLORS = ["#0A0A0A", "#525252", "#A3A3A3", "#059669", "#E11D48", "#F59E0B", "#78716C", "#404040"];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lowBooks, setLowBooks] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setData(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Koçan bitiyor uyarısı — 5'ten az yaprak kalanları getir
    api.get("/cheque-books")
      .then(({ data }) => {
        const low = (data || []).filter((b) => b.unused_count > 0 && b.unused_count < 5);
        setLowBooks(low);
      })
      .catch(() => {});
  }, [load]);

  if (loading || !data) return <div className="label-caps">Yükleniyor…</div>;

  const stats = [
    { label: "Toplam Bakiye", value: data.total_balance, tid: "stat-total-balance", icon: Wallet, tone: "black" },
    { label: "Bu Ay Gelir", value: data.month_income, tid: "stat-month-income", icon: ArrowDown, tone: "positive" },
    { label: "Bu Ay Gider", value: data.month_expense, tid: "stat-month-expense", icon: ArrowUp, tone: "negative" },
    { label: "Net Nakit Akışı", value: data.month_net, tid: "stat-month-net", icon: CalendarClock, tone: data.month_net >= 0 ? "positive" : "negative" },
  ];

  return (
    <div className="space-y-8">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps">Genel Bakış</p>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">
            Finansal Panel
          </h1>
        </div>
        <div className="text-xs text-neutral-500 border border-black/10 px-3 py-2 bg-white">
          {new Intl.DateTimeFormat("tr-TR", { dateStyle: "full" }).format(new Date())}
        </div>
      </header>

      {/* Stat cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <motion.div
            key={s.tid}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            data-testid={s.tid}
            className="brutal-card p-6"
          >
            <div className="flex items-start justify-between">
              <span className="label-caps">{s.label}</span>
              <s.icon size={16} strokeWidth={1.5} className="text-neutral-500" />
            </div>
            <div
              className={`font-mono-num text-3xl md:text-4xl font-semibold mt-4 ${
                s.tone === "positive" ? "text-emerald-700" : s.tone === "negative" ? "text-rose-600" : ""
              }`}
            >
              {fmtTRY(s.value)}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Koçan bitiyor uyarısı */}
      {lowBooks.length > 0 && (
        <div className="brutal-card p-4 bg-amber-50 border-2 border-amber-400" data-testid="dashboard-low-books">
          <div className="flex items-start gap-3">
            <BookOpen size={18} className="text-amber-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-900 text-sm">Koçan Bitiyor</p>
              <p className="text-xs text-amber-800 mt-0.5">
                Aşağıdaki bankalar için 5'ten az kullanılmamış yaprağınız kaldı. Yeni koçan siparişi vermeyi düşünün.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {lowBooks.map((b) => (
                  <span key={b.id} className="text-[11px] px-2 py-1 border border-amber-500 bg-white font-mono-num">
                    <b>{b.bank_name}</b> · {b.start_no}-{b.end_no} · <span className="text-rose-600 font-semibold">{b.unused_count}</span> yaprak
                  </span>
                ))}
              </div>
            </div>
            <Link
              to="/cek-kocanlari"
              data-testid="dashboard-low-books-link"
              className="text-xs underline decoration-amber-700 underline-offset-4 text-amber-900 shrink-0 whitespace-nowrap"
            >
              Koçanları Yönet →
            </Link>
          </div>
        </div>
      )}

      {/* Kritik Stok Uyarısı */}
      {data.low_stock_count > 0 && (
        <div className="brutal-card p-4 bg-rose-50 border-2 border-rose-400" data-testid="dashboard-low-stock">
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="text-rose-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-rose-900 text-sm">
                Kritik Stok · <span className="font-mono-num">{data.low_stock_count}</span> ürün
              </p>
              <p className="text-xs text-rose-800 mt-0.5">
                Aşağıdaki stok kalemleri minimum stok seviyesinin altına düştü. Üretim/sipariş planı yapın.
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {data.low_stock_items.map((s) => (
                  <span key={s.id} className="text-[11px] px-2 py-1 border border-rose-500 bg-white font-mono-num"
                        data-testid={`dashboard-low-stock-item-${s.id}`}>
                    <b>{s.sku}</b> · {s.name} ·{" "}
                    <span className="text-rose-700 font-semibold">{fmtTRY(s.qty_on_hand).replace("₺", "")}</span>
                    /{fmtTRY(s.min_stock).replace("₺", "")} {s.unit}
                  </span>
                ))}
              </div>
            </div>
            <Link
              to="/stok"
              data-testid="dashboard-low-stock-link"
              className="text-xs underline decoration-rose-700 underline-offset-4 text-rose-900 shrink-0 whitespace-nowrap"
            >
              Stokları Yönet →
            </Link>
          </div>
        </div>
      )}

      {/* Yapılacaklar widget */}
      <TodosWidget onChange={load} data={data} />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="brutal-card p-6 lg:col-span-2">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="label-caps">Nakit Akışı</p>
              <h3 className="font-display text-xl font-bold mt-1">Son 12 Ay</h3>
            </div>
            <div className="flex gap-3 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-emerald-600 inline-block" />Gelir</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-rose-500 inline-block" />Gider</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-black inline-block" />Net</span>
            </div>
          </div>
          <div className="mt-6 h-[280px]">
            <ResponsiveContainer>
              <AreaChart data={data.cashflow.map(c => ({ ...c, m: fmtMonth(c.month) }))}>
                <defs>
                  <linearGradient id="gI" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#059669" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="gE" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#E11D48" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#E11D48" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 3" stroke="#e5e5e5" />
                <XAxis dataKey="m" tick={{ fontSize: 11, fill: "#525252" }} />
                <YAxis tick={{ fontSize: 11, fill: "#525252" }} tickFormatter={(v) => Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
                <Tooltip formatter={(v) => fmtTRY(v)} />
                <Area type="monotone" dataKey="income" stroke="#059669" fill="url(#gI)" strokeWidth={1.8} />
                <Area type="monotone" dataKey="expense" stroke="#E11D48" fill="url(#gE)" strokeWidth={1.8} />
                <Line type="monotone" dataKey="net" stroke="#0A0A0A" strokeWidth={2} dot={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="brutal-card p-6">
          <p className="label-caps">Gider Dağılımı</p>
          <h3 className="font-display text-xl font-bold mt-1">Bu Ay</h3>
          <div className="mt-4 h-[280px]">
            {data.categories.length === 0 ? (
              <EmptyChart />
            ) : (
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.categories} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                    {data.categories.map((c, i) => (
                      <Cell key={c.name} fill={CHART_COLORS[i % CHART_COLORS.length]} stroke="#fff" strokeWidth={1} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v) => fmtTRY(v)} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Turkey Map — Coğrafi Cari Dağılımı */}
      <TurkeyMap />

      {/* Kâr Trendi — Son 6 Ay */}
      <ProfitTrendCard months={6} />

      {/* Accounts + Upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="brutal-card p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="label-caps">Hesaplar</p>
              <h3 className="font-display text-xl font-bold mt-1">Bakiyeler</h3>
            </div>
            <Link to="/hesaplar" className="text-xs underline decoration-black/40 underline-offset-4">Tümü →</Link>
          </div>
          <div className="mt-4 divide-y divide-black/5">
            {data.accounts.length === 0 && (
              <div className="text-sm text-neutral-500 py-6">Henüz hesap yok. <Link to="/hesaplar" className="underline">Hesap ekleyin</Link>.</div>
            )}
            {data.accounts.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div className="w-2.5 h-8" style={{ background: a.color || "#0A0A0A" }} />
                  <div>
                    <div className="text-sm font-medium">{a.name}</div>
                    <div className="text-[11px] text-neutral-500 uppercase tracking-widest">
                      {a.bank_name || (a.account_type === "cash" ? "Nakit" : "Banka")}
                    </div>
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${a.balance < 0 ? "text-rose-600" : ""}`}>
                  {fmtTRY(a.balance)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="brutal-card p-6">
          <div className="flex items-baseline justify-between">
            <div>
              <p className="label-caps">Yaklaşan</p>
              <h3 className="font-display text-xl font-bold mt-1">Sonraki 30 Gün</h3>
            </div>
            <Link to="/yaklasan" className="text-xs underline decoration-black/40 underline-offset-4">Tümü →</Link>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div className="border border-black/10 p-3 bg-white">
              <div className="label-caps">Ödemeler</div>
              <div className="font-mono-num text-xl mt-2 text-rose-600">{fmtTRY(data.upcoming_payments_total)}</div>
            </div>
            <div className="border border-black/10 p-3 bg-white">
              <div className="label-caps">Tahsilatlar</div>
              <div className="font-mono-num text-xl mt-2 text-emerald-700">{fmtTRY(data.upcoming_collections_total)}</div>
            </div>
          </div>
          <div className="mt-4 divide-y divide-black/5">
            {data.upcoming.length === 0 && <div className="text-sm text-neutral-500 py-6">Yaklaşan kayıt yok.</div>}
            {data.upcoming.map((u) => (
              <div key={u.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium">{u.title}</div>
                  <div className="text-[11px] text-neutral-500 uppercase tracking-widest">
                    {u.kind === "payment" ? "Ödeme" : "Tahsilat"} · {u.due_date}
                  </div>
                </div>
                <div className={`font-mono-num font-semibold ${u.kind === "payment" ? "text-rose-600" : "text-emerald-700"}`}>
                  {fmtTRY(u.amount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Çekler mini widget */}
      <div className="brutal-card p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <p className="label-caps">Çek Portföyü</p>
            <h3 className="font-display text-xl font-bold mt-1">Açık Çekler</h3>
          </div>
          <Link to="/cekler" className="text-xs underline decoration-black/40 underline-offset-4" data-testid="dashboard-cheques-link">
            Tümü →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Alınan Çekler</div>
            <div className="font-mono-num text-2xl mt-2 text-emerald-700">{fmtTRY(data.cheques_received_total || 0)}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Verilen Çekler</div>
            <div className="font-mono-num text-2xl mt-2 text-rose-600">{fmtTRY(data.cheques_issued_total || 0)}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Açık Çek Sayısı</div>
            <div className="font-mono-num text-2xl mt-2">{data.cheques_open_count || 0}</div>
          </div>
        </div>
      </div>

      {/* Krediler mini widget */}
      <div className="brutal-card p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <p className="label-caps">Kredi Yükümlülükleri</p>
            <h3 className="font-display text-xl font-bold mt-1">Aktif Krediler</h3>
          </div>
          <Link to="/krediler" className="text-xs underline decoration-black/40 underline-offset-4" data-testid="dashboard-loans-link">
            Tümü →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Aktif Kredi</div>
            <div className="font-mono-num text-2xl mt-2">{data.loans_active_count || 0}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Toplam Anapara Borç</div>
            <div className="font-mono-num text-2xl mt-2 text-rose-600">{fmtTRY(data.loans_total_debt || 0)}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Bu Ay Ödenecek</div>
            <div className="font-mono-num text-2xl mt-2">{fmtTRY(data.loans_month_obligation || 0)}</div>
          </div>
        </div>
      </div>

      {/* Teminat Mektupları mini widget */}
      <div className="brutal-card p-6">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <p className="label-caps">Bankadan Alınan Garantiler</p>
            <h3 className="font-display text-xl font-bold mt-1">Teminat Mektupları</h3>
          </div>
          <Link to="/teminat-mektuplari" className="text-xs underline decoration-black/40 underline-offset-4" data-testid="dashboard-guarantees-link">
            Tümü →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Aktif TM</div>
            <div className="font-mono-num text-2xl mt-2">{data.guarantees_active_count || 0}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">Aktif TM Tutarı</div>
            <div className="font-mono-num text-2xl mt-2">{fmtTRY(data.guarantees_active_amount || 0)}</div>
          </div>
          <div className="border border-black/10 p-4 bg-white">
            <div className="label-caps">30 Gün İçinde Dolan</div>
            <div className="font-mono-num text-2xl mt-2 text-amber-600">{fmtTRY(data.guarantees_expiring_30_amount || 0)}</div>
            <div className="text-[10px] text-neutral-500 mt-0.5">{data.guarantees_expiring_30_count || 0} mektup</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EmptyChart() {
  return (
    <div className="h-full flex items-center justify-center text-sm text-neutral-500">
      Bu ay için gider verisi yok.
    </div>
  );
}

function TodosWidget({ data, onChange }) {
  const [quickTitle, setQuickTitle] = useState("");
  const [contactId, setContactId] = useState("");
  const [accountId, setAccountId] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/contacts"), api.get("/accounts")]).then(([c, a]) => {
      setContacts(c.data); setAccounts(a.data);
    });
  }, []);

  const quickAdd = async (e) => {
    e.preventDefault();
    if (!quickTitle.trim()) return;
    setSaving(true);
    try {
      await api.post("/todos", {
        title: quickTitle.trim(),
        contact_id: contactId || null,
        account_id: accountId || null,
        due_date: dueDate || null,
        priority: "medium",
      });
      setQuickTitle(""); setContactId(""); setAccountId(""); setDueDate("");
      toast.success("Görev eklendi");
      onChange();
    } catch {
      toast.error("Eklenemedi");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (id) => {
    await api.post(`/todos/${id}/toggle`);
    onChange();
  };

  return (
    <div className="brutal-card p-6" data-testid="dashboard-todos-widget">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ListChecks size={18} strokeWidth={1.6} />
          <div>
            <p className="label-caps">Yapılacaklar</p>
            <h3 className="font-display text-xl font-bold mt-0.5">
              {data.todos_open_count || 0} açık görev
              {data.todos_overdue_count > 0 && (
                <span className="ml-2 text-xs text-rose-600 font-normal">
                  · {data.todos_overdue_count} gecikmiş
                </span>
              )}
            </h3>
          </div>
        </div>
        <Link
          to="/yapilacaklar"
          data-testid="dashboard-todos-link"
          className="text-xs underline decoration-black/40 underline-offset-4"
        >
          Tümü →
        </Link>
      </div>

      {/* Quick-add form */}
      <form onSubmit={quickAdd} className="mt-4 border border-black/10 bg-neutral-50 p-3 space-y-2" data-testid="dashboard-todo-quickadd">
        <div className="flex gap-2">
          <input
            data-testid="dashboard-todo-title"
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            placeholder="Yeni görev başlığı… (örn. Ali A.Ş. çeki teslim al)"
            className="flex-1 border border-black/20 px-3 py-2 outline-none focus:ring-2 focus:ring-black text-sm bg-white"
          />
          <button
            data-testid="dashboard-todo-add"
            disabled={saving || !quickTitle.trim()}
            className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Plus size={14} /> Ekle
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <select
            data-testid="dashboard-todo-contact"
            value={contactId} onChange={(e) => setContactId(e.target.value)}
            className="border border-black/20 px-2 py-1.5 outline-none text-xs bg-white"
          >
            <option value="">İlgili cari — seçim yok</option>
            {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select
            data-testid="dashboard-todo-account"
            value={accountId} onChange={(e) => setAccountId(e.target.value)}
            className="border border-black/20 px-2 py-1.5 outline-none text-xs bg-white"
          >
            <option value="">İlgili banka/hesap — seçim yok</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <input
            data-testid="dashboard-todo-date"
            type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
            className="border border-black/20 px-2 py-1.5 outline-none text-xs"
          />
        </div>
      </form>

      {/* Preview list */}
      <div className="mt-3">
        {(data.todos_preview || []).length === 0 ? (
          <div className="text-sm text-neutral-500 py-4 text-center">
            Açık görev yok. 🎯 Boş bir liste, temiz bir zihin.
          </div>
        ) : (
          <ul className="divide-y divide-black/5">
            {(data.todos_preview || []).map((t) => (
              <TodoRow
                key={t.id}
                todo={t}
                contact={contacts.find((c) => c.id === t.contact_id)}
                account={accounts.find((a) => a.id === t.account_id)}
                onToggle={() => toggle(t.id)}
                dense
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
